<template>
  <div
    class="main-menu menu-fixed menu-accordion menu-shadow"
    :class="[
      {
        expanded:
          !isVerticalMenuCollapsed ||
          (isVerticalMenuCollapsed && isMouseHovered),
      },
      skin === 'semi-dark' ? 'menu-dark' : 'menu-light',
    ]"
    @mouseenter="updateMouseHovered(true)"
    @mouseleave="updateMouseHovered(false)"
  >
    <!-- main menu header-->
    <div class="navbar-header expanded">
      <slot
        name="header"
        :toggleVerticalMenuActive="toggleVerticalMenuActive"
        :toggleCollapsed="toggleCollapsed"
        :collapseTogglerIcon="collapseTogglerIcon"
      >
        <ul class="nav navbar-nav flex-row">
          <!-- Logo & Text -->
          <li class="nav-item mr-auto">
            <b-link class="navbar-brand" :to="homePath">
              <!-- <span class="brand-logo">
                <b-img
                  :src="appLogoImage"
                  alt="logo"
                />
              </span> -->
              <h2 class="brand-text">
                {{ appName }}
              </h2>
            </b-link>
          </li>

          <!-- Toggler Button -->
          <li class="nav-item nav-toggle">
            <b-link class="nav-link modern-nav-toggle">
              <feather-icon
                icon="XIcon"
                size="20"
                class="d-block d-xl-none"
                @click="toggleVerticalMenuActive"
              />
              <feather-icon
                :icon="collapseTogglerIconFeather"
                size="20"
                class="d-none d-xl-block collapse-toggle-icon"
                @click="toggleCollapsed"
              />
            </b-link>
          </li>
        </ul>
      </slot>
    </div>
    <!-- / main menu header-->

    <!-- Shadow -->
    <div :class="{ 'd-block': shallShadowBottom }" class="shadow-bottom" />

    <!-- main menu content-->
    <vue-perfect-scrollbar
      :settings="perfectScrollbarSettings"
      class="main-menu-content scroll-area"
      tagname="ul"
      @ps-scroll-y="
        (evt) => {
          shallShadowBottom = evt.srcElement.scrollTop > 0
        }
      "
    >
      <vertical-nav-menu-items
        :items="sidebarMenuList"
        class="navigation navigation-main d-none d-xl-block"
      />
      <vertical-nav-menu-items
        :items="allMenuList"
        class="navigation navigation-main d-xl-none"
      />
    </vue-perfect-scrollbar>
    <!-- /main menu content-->
  </div>
</template>

<script>
// * Start Import Custom Menu
import {
  WPM_CUSTOMER_DETAIL,
  WPM_TICKET_MANAGEMENT_MENU,
  WPM_QR_ACCESS_MANAGEMENT_MENU,
  WPM_SMART_HAND_MENU,
  WPM_PARCEL_TRACKING_MENU,
  WPM_CROSS_CONNECT_MENU,
  INTERNAL_WPM_TICKET_MANAGEMENT_MENU,
  INTERNAL_WPM_SITE_ACCESS_MANAGEMENT_MENU,
  INTERNAL_WPM_INVENTORY_MENU,
  INTERNAL_WPM_SMART_HAND_MENU,
  INTERNAL_WPM_PARCEL_TRACKING_MENU,
  INTERNAL_WPM_CROSS_CONNECT_MENU,
  INTERNAL_WPM_IDC_MENU,
  INTERNAL_WPM_IP_ADDRESS_MANAGEMENT_MENU,
  INTERNAL_REPORT_MENU,
  INTERNAL_ESCALATION_MENU,
  INTERNAL_WPM_DASHBOARD_MENU,
  INTERNAL_WPM_EXECUTIVE_DASHBOARD_MENU,
  INTERNAL_WORKLOAD_MENU,
} from "@/navigation/vertical/web-portal-menu"
import {
  SMS_DASHBOARD_MENU,
  SMS_ALL_EVENT_MENU,
  SMS_SERVER_MANAGEMENT_MENU,
  SMS_ALERT_MENU,
  SMS_REPORT_MENU,
  SMS_DISCOVERY_MENU,
  SMS_POLICY_MENU,
} from "@/navigation/vertical/server-management-menu"
import {
  BMM_BACKUP_MENU,
  BMM_CREDENTIAL_MENU,
  BMM_DASHBOARD_MENU,
  BMM_REPOSITORY_MENU,
  BMM_SERVER_MANAGEMENT_MENU,
  BMM_VEEAM_MENU,
} from "@/navigation/vertical/backup-management-menu"
import {
  NCM_DASHBOARD_MENU,
  NCM_NODE_MANAGEMENT_MENU,
  NCM_CONFIGURATION_HISTORY_MENU,
  NCM_CONFIGURATION_TEMPLATE_MENU,
  NCM_CONFIGURATION_COMPARISON_MENU,
  NCM_DISCOVERY_MENU,
  NCM_CREDENTIAL_MENU,
  NCM_BACKUP_SETTINGS_MENU,
} from "@/navigation/vertical/network-configuration-menu"
import {
  INTERNAL_GLOBAL_SETTING_SERVICE_DATA_MANAGEMENT_MENU,
  INTERNAL_GLOBAL_SETTING_TICKET_DATA_MANAGEMENT_MENU,
  INTERNAL_GLOBAL_SETTING_LOCATION_DATA_MANAGEMENT_MENU,
} from "@/navigation/vertical/global-settings-menu"
// * End Import Custom Menu

import VerticalNavMenuItems from "./components/vertical-nav-menu-items/VerticalNavMenuItems.vue"
import VuePerfectScrollbar from "vue-perfect-scrollbar"
import useAppConfig from "@core/app-config/useAppConfig"
import useVerticalNavMenu from "./useVerticalNavMenu"
import { BLink, BImg } from "bootstrap-vue"
import { mapGetters } from "vuex"
import { provide, computed, ref } from "@vue/composition-api"
import mixinsValidator from "@/mixins/mixinsValidator"
import mixinsObjectHelper from "@/mixins/mixinsObjectHelper"
import mixinsUser from "@/mixins/mixinsUser"
import { PermissionUser } from "@/constants/permission-user"

export default {
  components: {
    VuePerfectScrollbar,
    VerticalNavMenuItems,
    BLink,
    BImg,
  },
  props: {
    isVerticalMenuActive: {
      type: Boolean,
      required: true,
    },
    toggleVerticalMenuActive: {
      type: Function,
      required: true,
    },
  },
  mixins: [mixinsValidator, mixinsObjectHelper, mixinsUser],
  data() {
    return {
      WPMSidebar: [],
      internalWPMSidebar: [],
      internalSMSSidebar: [],
      internalBMMSidebar: [],
      internalNCMSidebar: [],
      internalGlobalSettingDataManagementSidebar: [],
    }
  },
  computed: {
    ...mapGetters("verticalMenu", [
      "getCurrentModuleNavbar",
      "getWebPortalModuleEnabledSidebar",
      "getInternalWebPortalModuleEnabledSidebar",
      "getInternalServerManagementModuleEnabledSidebar",
      "getInternalBackupManagementModuleEnabledSidebar",
      "getInternalNetworkConfigurationModuleEnabledSidebar",
    ]),
    ...mapGetters("appPermission", [
      "getInternalUserPermission",
      "getInternalUserRole",
      "getUserPermission",
    ]),
    isInternalUser() {
      return this.$route.meta.type === "internal"
    },
    currentModule() {
      return this.$route.meta?.module
    },
    homePath() {
      return this.isInternalUser ? "/internal" : "/"
    },
    internalUserRole() {
      return this.getInternalUserRole
    },
    internalPermission() {
      return this.getInternalUserPermission
    },
    userPermission() {
      return this.getUserPermission
    },
    WPMEnabledSidebar() {
      return this.getWebPortalModuleEnabledSidebar
    },
    internalWPMEnabledSidebar() {
      return this.getInternalWebPortalModuleEnabledSidebar
    },
    internalSMSEnabledSidebar() {
      return this.getInternalServerManagementModuleEnabledSidebar
    },
    internalBMMEnabledSidebar() {
      return this.getInternalBackupManagementModuleEnabledSidebar
    },
    internalNCMEnabledSidebar() {
      return this.getInternalNetworkConfigurationModuleEnabledSidebar
    },
    sidebarMenuList() {
      switch (this.getCurrentModuleNavbar) {
        case "webPortal":
          return this.isInternalUser ? this.internalWPMSidebar : this.WPMSidebar
        case "serverManagement":
          return this.internalSMSSidebar
        case "backupManagement":
          return this.internalBMMSidebar
        case "networkConfiguration":
          return this.internalNCMSidebar
        case "globalSettingsDataManagement":
          return this.internalGlobalSettingDataManagementSidebar
        default:
          return []
      }
    },
    allGlobalSettingMenuList() {
      return [
        { icon: "HomeIcon", title: "Home Page", route: "internalHome" },
        { header: "Settings" },
        {
          icon: "SettingsIcon",
          title: "Global Settings",
          route: "globalSettings",
        },
        { header: "Data Management" },
        INTERNAL_GLOBAL_SETTING_SERVICE_DATA_MANAGEMENT_MENU,
        INTERNAL_GLOBAL_SETTING_TICKET_DATA_MANAGEMENT_MENU,
        INTERNAL_GLOBAL_SETTING_LOCATION_DATA_MANAGEMENT_MENU,
      ]
    },
    allMenuList() {
      if (
        this.getCurrentModuleNavbar === "globalSettings" ||
        this.getCurrentModuleNavbar === "globalSettingsDataManagement"
      ) {
        return this.allGlobalSettingMenuList
      } else if (this.isInternalUser) {
        let allInternalSideBar = [
          { icon: "HomeIcon", title: "Home Page", route: "internalHome" },
        ]
        if (this.internalWPMSidebar.length > 1) {
          allInternalSideBar.push(this.internalWPMSidebar)
        }
        if (this.internalSMSSidebar.length > 1) {
          allInternalSideBar.push(this.internalSMSSidebar)
        }
        if (this.internalBMMSidebar.length > 1) {
          allInternalSideBar.push(this.internalBMMSidebar)
        }
        if (this.internalNCMSidebar.length > 1) {
          allInternalSideBar.push(this.internalNCMSidebar)
        }
        allInternalSideBar = allInternalSideBar.flatMap((module) => module)
        return allInternalSideBar
      } else {
        return [...this.WPMSidebar]
      }
    },
  },
  watch: {
    WPMEnabledSidebar: {
      immediate: true,
      deep: true,
      handler() {
        this.setWebPortalSidebarMenu()
      },
    },
    internalWPMEnabledSidebar: {
      immediate: true,
      deep: true,
      handler() {
        this.setWebPortalSidebarMenu()
      },
    },
    internalSMSEnabledSidebar: {
      immediate: true,
      deep: true,
      handler() {
        this.setServerManagementSidebarMenu()
      },
    },
    internalBMMEnabledSidebar: {
      immediate: true,
      deep: true,
      handler() {
        this.setBackupManagementMenu()
      },
    },
    internalNCMEnabledSidebar: {
      immediate: true,
      deep: true,
      handler() {
        this.setNetworkConfigurationMenu()
      },
    },
  },
  mounted() {
    this.setWebPortalSidebarMenu()
    this.setServerManagementSidebarMenu()
    this.setBackupManagementMenu()
    this.setNetworkConfigurationMenu()
    this.setGlobalSettingsDataManagementMenu()
  },
  beforeUpdate() {
    this.setWebPortalSidebarMenu()
    this.setServerManagementSidebarMenu()
    this.setBackupManagementMenu()
    this.setNetworkConfigurationMenu()
    this.setGlobalSettingsDataManagementMenu()
  },
  methods: {
    setWebPortalSidebarMenu() {
      this.WPMSidebar = [{ header: "Web Portal" }]
      // * Set menu for external user
      this.WPMSidebar.push(WPM_CUSTOMER_DETAIL)
      if (this.WPMEnabledSidebar.wpmTicketTrackingAccess) {
        this.WPMSidebar.push(WPM_TICKET_MANAGEMENT_MENU)
      }
      if (this.WPMEnabledSidebar.wpmQrAccessAccess) {
        this.WPMSidebar.push(WPM_QR_ACCESS_MANAGEMENT_MENU)
      }
      // if (this.WPMEnabledSidebar.wpmSmartHandManagementAccess) {
      //   this.WPMSidebar.push(WPM_SMART_HAND_MENU)
      // }
      // if (this.WPMEnabledSidebar.wpmCrossConnectAccess) {
      //   this.WPMSidebar.push(WPM_CROSS_CONNECT_MENU)
      // }
      // if (this.WPMEnabledSidebar.wpmParcelTrackingAccess) {
      //   this.WPMSidebar.push(WPM_PARCEL_TRACKING_MENU)
      // }

      // * Set menu for internal user
      this.internalWPMSidebar = [{ header: "Web Portal" }]
      if (this.internalWPMEnabledSidebar.wpmDashboardTechnicalAccess) {
        this.internalWPMSidebar.push(INTERNAL_WPM_DASHBOARD_MENU)
      }
      if (this.internalWPMEnabledSidebar.wpmDashboardExecutiveAccess) {
        this.internalWPMSidebar.push(INTERNAL_WPM_EXECUTIVE_DASHBOARD_MENU)
      }
      if (this.internalWPMEnabledSidebar.wpmTicketTrackingAccess) {
        this.internalWPMSidebar.push(INTERNAL_WPM_TICKET_MANAGEMENT_MENU)
      }
      if (this.internalWPMEnabledSidebar.wpmTicketEscalationAccess) {
        this.internalWPMSidebar.push(INTERNAL_ESCALATION_MENU)
      }
      const siteAccessSidebarMenu = this.getSiteAccessSideBarMenu()
      if (siteAccessSidebarMenu.children.length) {
        this.internalWPMSidebar.push(siteAccessSidebarMenu)
      }
      // if (this.internalWPMEnabledSidebar.wpmSmartHandManagementAccess) {
      //   this.internalWPMSidebar.push(INTERNAL_WPM_SMART_HAND_MENU)
      // }
      // if (this.internalWPMEnabledSidebar.wpmCrossConnectAccess) {
      //   this.internalWPMSidebar.push(INTERNAL_WPM_CROSS_CONNECT_MENU)
      // }
      // if (this.internalWPMEnabledSidebar.wpmParcelTrackingAccess) {
      //   this.internalWPMSidebar.push(INTERNAL_WPM_PARCEL_TRACKING_MENU)
      // }
      // if (this.internalWPMEnabledSidebar.wpmInventoryManagementAccess) {
      //   this.internalWPMSidebar.push(INTERNAL_WPM_INVENTORY_MENU)
      // }
      const idcSidebarMenu = this.getIdcSidebarMenu()
      if (idcSidebarMenu.children.length) {
        this.internalWPMSidebar.push(idcSidebarMenu)
      }
      if (this.internalWPMEnabledSidebar.wpmIpamAccess) {
        this.internalWPMSidebar.push(INTERNAL_WPM_IP_ADDRESS_MANAGEMENT_MENU)
      }
      if (this.internalWPMEnabledSidebar.wpmReportAccess) {
        this.internalWPMSidebar.push(INTERNAL_REPORT_MENU)
      }
      if (this.internalWPMEnabledSidebar.wpmWorkloadAccess) {
        this.internalWPMSidebar.push(INTERNAL_WORKLOAD_MENU)
      }
    },

    getSiteAccessSideBarMenu() {
      let siteAccessMenu = this.deepCopyObject(
        INTERNAL_WPM_SITE_ACCESS_MANAGEMENT_MENU
      )
      if (!this.internalWPMEnabledSidebar.wpmQrAccessAccess) {
        const indexToRemove = siteAccessMenu.children.findIndex(
          (item) => item.title === "Access List"
        )
        if (indexToRemove !== -1) {
          siteAccessMenu.children.splice(indexToRemove, 1)
        }
      }
      if (!this.internalWPMEnabledSidebar.siteAccessDashboard) {
        const indexToRemove = siteAccessMenu.children.findIndex(
          (item) => item.title === "Dashboard"
        )
        if (indexToRemove !== -1) {
          siteAccessMenu.children.splice(indexToRemove, 1)
        }
      }

      return siteAccessMenu
    },

    getIdcSidebarMenu() {
      let idcMenu = this.deepCopyObject(INTERNAL_WPM_IDC_MENU)
      if (!this.internalWPMEnabledSidebar.wpmIdcDatabaseAccess) {
        const indexToRemove = idcMenu.children.findIndex(
          (item) => item.title === "Contract"
        )
        if (indexToRemove !== -1) {
          idcMenu.children.splice(indexToRemove, 1)
        }
      }
      if (!this.internalWPMEnabledSidebar.wpmIdcCustomerAccess) {
        const indexToRemove = idcMenu.children.findIndex(
          (item) => item.title === "Customer"
        )
        if (indexToRemove !== -1) {
          idcMenu.children.splice(indexToRemove, 1)
        }
      }
      if (!this.internalWPMEnabledSidebar.wpmIdcContactPointAccess) {
        const indexToRemove = idcMenu.children.findIndex(
          (item) => item.title === "Contact Point & Sale"
        )
        if (indexToRemove !== -1) {
          idcMenu.children.splice(indexToRemove, 1)
        }
      }
      if (!this.internalWPMEnabledSidebar.wpmContractAttachmentAccess) {
        const indexToRemove = idcMenu.children.findIndex(
          (item) => item.title === "Attachment"
        )
        if (indexToRemove !== -1) {
          idcMenu.children.splice(indexToRemove, 1)
        }
      }
      return idcMenu
    },

    setServerManagementSidebarMenu() {
      this.internalSMSSidebar = [
        {
          header: "Physical Servers and VMware",
        },
      ]
      if (this.internalSMSEnabledSidebar.smsDashboardAccess) {
        this.internalSMSSidebar.push(SMS_DASHBOARD_MENU)
      }
      if (this.internalSMSEnabledSidebar.smsAllEventsAccess) {
        this.internalSMSSidebar.push(SMS_ALL_EVENT_MENU)
      }
      if (this.internalSMSEnabledSidebar.smsServerManagementAccess) {
        this.internalSMSSidebar.push(SMS_SERVER_MANAGEMENT_MENU)
      }
      if (this.internalSMSEnabledSidebar.smsAlertAccess) {
        this.internalSMSSidebar.push(SMS_ALERT_MENU)
      }
      if (this.internalSMSEnabledSidebar.smsReportAccess) {
        this.internalSMSSidebar.push(SMS_REPORT_MENU)
      }
      if (this.internalSMSEnabledSidebar.smsDiscoveryAccess) {
        this.internalSMSSidebar.push(SMS_DISCOVERY_MENU)
      }
      if (this.internalSMSEnabledSidebar.smsPolicyAccess) {
        this.internalSMSSidebar.push(SMS_POLICY_MENU)
      }
    },

    setBackupManagementMenu() {
      this.internalBMMSidebar = [
        {
          header: "Backup Management",
        },
      ]
      if (this.internalBMMEnabledSidebar.bmmDashboardAccess) {
        this.internalBMMSidebar.push(BMM_DASHBOARD_MENU)
      }
      if (this.internalBMMEnabledSidebar.bmmManagedServerAccess) {
        this.internalBMMSidebar.push(BMM_SERVER_MANAGEMENT_MENU)
      }
      if (this.internalBMMEnabledSidebar.bmmRepositoryAccess) {
        this.internalBMMSidebar.push(BMM_REPOSITORY_MENU)
      }
      if (this.internalBMMEnabledSidebar.bmmBackupAndRestoreAccess) {
        this.internalBMMSidebar.push(BMM_BACKUP_MENU)
      }
      if (this.internalBMMEnabledSidebar.bmmCredentialAccess) {
        this.internalBMMSidebar.push(BMM_CREDENTIAL_MENU)
      }
      if (this.internalBMMEnabledSidebar.bmmVeeamAccess) {
        this.internalBMMSidebar.push(BMM_VEEAM_MENU)
      }
    },

    setNetworkConfigurationMenu() {
      this.internalNCMSidebar = [
        {
          header: "Network Configuration",
        },
      ]
      if (this.internalNCMEnabledSidebar.ncmDashboardAccess) {
        this.internalNCMSidebar.push(NCM_DASHBOARD_MENU)
      }
      if (this.internalNCMEnabledSidebar.ncmNodeManagementAccess) {
        this.internalNCMSidebar.push(NCM_NODE_MANAGEMENT_MENU)
      }
      if (this.internalNCMEnabledSidebar.ncmConfigHistoryAccess) {
        this.internalNCMSidebar.push(NCM_CONFIGURATION_HISTORY_MENU)
      }
      if (this.internalNCMEnabledSidebar.ncmConfigTemplateAccess) {
        this.internalNCMSidebar.push(NCM_CONFIGURATION_TEMPLATE_MENU)
      }
      if (this.internalNCMEnabledSidebar.ncmConfigComparisonAccess) {
        this.internalNCMSidebar.push(NCM_CONFIGURATION_COMPARISON_MENU)
      }
      if (this.internalNCMEnabledSidebar.ncmDiscoveryNodeAccess) {
        this.internalNCMSidebar.push(NCM_DISCOVERY_MENU)
      }
      if (this.internalNCMEnabledSidebar.ncmCredentialAccess) {
        this.internalNCMSidebar.push(NCM_CREDENTIAL_MENU)
      }
      if (this.internalNCMEnabledSidebar.ncmBackupSettingsAccess) {
        this.internalNCMSidebar.push(NCM_BACKUP_SETTINGS_MENU)
      }
    },

    setGlobalSettingsDataManagementMenu() {
      if (this.hasPermission(PermissionUser.SETTINGS_READ)) {
        this.internalGlobalSettingDataManagementSidebar = [
          {
            header: "Data Management",
          },
          INTERNAL_GLOBAL_SETTING_SERVICE_DATA_MANAGEMENT_MENU,
          INTERNAL_GLOBAL_SETTING_TICKET_DATA_MANAGEMENT_MENU,
          INTERNAL_GLOBAL_SETTING_LOCATION_DATA_MANAGEMENT_MENU,
        ]
      }
    },
  },
  setup(props) {
    const {
      isMouseHovered,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      updateMouseHovered,
    } = useVerticalNavMenu(props)

    const { skin } = useAppConfig()

    // Shadow bottom is UI specific and can be removed by user => It's not in `useVerticalNavMenu`
    const shallShadowBottom = ref(false)

    provide("isMouseHovered", isMouseHovered)

    const perfectScrollbarSettings = {
      maxScrollbarLength: 60,
      wheelPropagation: false,
    }

    const collapseTogglerIconFeather = computed(() =>
      collapseTogglerIcon.value === "unpinned" ? "CircleIcon" : "DiscIcon"
    )

    // App Name
    //const { appName, appLogoImage } = $themeConfig
    const appName = "Forest"

    return {
      perfectScrollbarSettings,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      isMouseHovered,
      updateMouseHovered,
      collapseTogglerIconFeather,

      // Shadow Bottom
      shallShadowBottom,

      // Skin
      skin,

      // App Name
      appName,
      //appLogoImage,
    }
  },
}
</script>

<style lang="scss">
@import "~@core/scss/base/core/menu/menu-types/vertical-menu.scss";
</style>
